import { useEffect, useState } from 'react'
import { useToastContext } from 'src/components/ui/ToastMessage/ToastContext'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { axiosClient } from 'src/services/axiosInstance'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { deleteCookie, getCookie } from 'src/utils/cookies'

import { EstructurePixPaymentToast } from './ToastPixPaymentContent'
import { useDelayedStartTime } from './useDelayedStartTime'

interface PixPaymentDetails {
  linkPix: string
  qrCodeBase64Image: string
  isThePaymentPending: boolean
  orderData: {
    productName: string
    productImage: string
    qttItems: number
    orderPrice: number
  }
}

interface CookieObject {
  createdAt: number
  orderGroup: string
}

function parseCookie(): CookieObject | null {
  try {
    const parsed = JSON.parse(getCookie('pixPaymentReminderTrigger') ?? '')

    return typeof parsed.createdAt === 'number' &&
      typeof parsed.orderGroup === 'string'
      ? parsed
      : null
  } catch {
    return null
  }
}

export const PixPaymentReminder = () => {
  const { sendToast } = useToastContext()
  const { orderForm } = useCheckout()
  const [paymentData, setPaymentData] = useState<PixPaymentDetails | null>(null)
  const [toastSent, setToastSent] = useState(false)

  const dataPixOrder = parseCookie()
  const { orderGroup, createdAt } = dataPixOrder ?? {}
  const { pageData } = useGlobalContext()

  const { toastStartTime, delay, isReady } = useDelayedStartTime({
    createdAt: createdAt ?? NaN,
  })

  useEffect(() => {
    if (paymentData || toastStartTime === null || toastSent) {
      return
    }

    if (!isReady && delay > 0) {
      const timer = setTimeout(fetchPixDetails, delay)

      return () => clearTimeout(timer)
    }

    if (isReady) {
      fetchPixDetails()
    }

    async function fetchPixDetails() {
      try {
        const response = await axiosClient.post('/api/pixPaymentDetails', {
          orderGroup,
        })

        const responsePaymentData = response.data.response

        if (
          (!responsePaymentData.linkPix &&
            !responsePaymentData.qrCodeBase64Image) ||
          !responsePaymentData.isThePaymentPending
        ) {
          deleteCookie('pixPaymentReminderTrigger')
          setToastSent(true)

          return
        }

        setPaymentData(responsePaymentData)
      } catch (error) {
        console.error('Error', error)
      }
    }
  }, [orderGroup, paymentData, toastStartTime, delay, isReady, toastSent])

  useEffect(() => {
    if (
      !paymentData ||
      !paymentData.linkPix ||
      !paymentData.qrCodeBase64Image ||
      toastStartTime === null ||
      toastSent
    ) {
      return
    }

    const standardObj = getStandardObject(pageData)

    const orderPrice = paymentData.orderData?.orderPrice ?? 0
    const cookieCreatedAt = createdAt ?? NaN

    const secondsSinceCreation = cookieCreatedAt
      ? Math.floor((Date.now() - cookieCreatedAt) / 1000)
      : 0

    sendToast({
      subtext: (
        <EstructurePixPaymentToast
          linkPix={paymentData?.linkPix}
          qrCodeBase64Image={paymentData?.qrCodeBase64Image}
          productsData={paymentData?.orderData}
        />
      ),
      variant: 'pixPaymentReminder',
      persist: true,
      orderFormInfo: orderForm,
      toastStartTime,
    })

    dispatchAmplitudeEvent({
      eventName: 'Payment Reminder Toast Displayed',
      eventData: {
        time_since_abandonment_seconds: secondsSinceCreation,
        number_cart_items: paymentData?.orderData?.qttItems,
        value_cart_items: orderPrice,
      },
      ...standardObj,
    })
    setToastSent(true)
    deleteCookie('pixPaymentReminderTrigger')
  }, [
    createdAt,
    orderForm,
    pageData,
    paymentData,
    sendToast,
    toastSent,
    toastStartTime,
  ])

  return null
}
